// Rooms list page — Heybase-style digital sales rooms.
// Top: welcome + template cards row (Blank + Starter).
// Below: All rooms table (search, status filter, star + delete actions).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Star, StarOff, Trash2, DoorOpen, Sparkles, Archive, ArrowRight, Circle, CheckCircle2, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import PragmaSelect from "../components/PragmaSelect";
import { useConfirm } from "../components/ConfirmModal";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  const now = new Date();
  const diffMin = (now - d) / 60000;
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
  if (diffMin < 60 * 24 * 30) return `${Math.floor(diffMin / (60 * 24))}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const BUILT_IN_TEMPLATES = [
  {
    id: "blank",
    label: "Blank room",
    hint: "Start from scratch",
    bg: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
    icon: Plus,
  },
  {
    id: "starter",
    label: "Sales room starter",
    hint: "6 sections · ready to edit",
    bg: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
    icon: Sparkles,
  },
];

const TEMPLATE_GRADIENTS = [
  "linear-gradient(135deg, #fef3c7, #fde68a)",
  "linear-gradient(135deg, #fee2e2, #fecaca)",
  "linear-gradient(135deg, #d1fae5, #a7f3d0)",
  "linear-gradient(135deg, #ede9fe, #ddd6fe)",
  "linear-gradient(135deg, #ffe4e6, #fecdd3)",
];

const STATUS_FILTER = [
  { value: "active", label: "Active" },
  { value: "starred", label: "Starred" },
  { value: "archived", label: "Archived" },
];

export default function Rooms() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [creating, setCreating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const firstName = user?.first_name || (user?.name || "").split(" ")[0] || "there";
  const canManageTemplates = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (status) params.set("status", status);
      const [roomsRes, tplRes] = await Promise.all([
        api.get(`/rooms?${params.toString()}`),
        api.get("/rooms/templates").catch(() => ({ data: { items: [] } })),
      ]);
      setRows(roomsRes.data.items || []);
      setTemplates(tplRes.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rooms");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const create = async (template) => {
    setCreating(true);
    try {
      const { data } = await api.post("/rooms", { from_template: template });
      toast.success("Room created");
      navigate(`/rooms/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const toggleStar = async (room) => {
    try {
      await api.patch(`/rooms/${room.id}`, { starred: !room.starred });
      setRows((r) => r.map((x) => x.id === room.id ? { ...x, starred: !x.starred } : x));
    } catch { toast.error("Failed"); }
  };

  const archiveRoom = async (room) => {
    const ok = await confirm({
      title: "Archive this room?",
      message: `"${room.name}" will move to Archived. The public share URL keeps working.`,
      confirmLabel: "Archive",
    });
    if (!ok) return;
    try {
      await api.delete(`/rooms/${room.id}`);
      toast.success("Room archived");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="rooms-page">
      <PageHeader title="Rooms" subtitle="branded deal rooms for buyers" />

      {/* Welcome + templates row */}
      <div className="px-8 pt-6">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }} data-testid="rooms-welcome">
              🎉 Welcome {firstName}!
            </h2>
            <div className="text-sm mt-1" style={{ color: "var(--pg-text-mute)" }}>
              Create a room to share proposals, videos and next steps with a buyer.
            </div>
          </div>
          {templates.length > 0 && (
            <button
              onClick={() => setShowTemplates(true)}
              data-testid="view-all-templates"
              className="pg-btn pg-btn-ghost"
              style={{ fontSize: 13 }}
            >
              View all templates <ArrowRight size={13} />
            </button>
          )}
        </div>
        <div
          data-testid="rooms-template-strip"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 14,
            marginBottom: 22,
          }}
        >
          {BUILT_IN_TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => create(t.id)}
                disabled={creating}
                data-testid={`template-card-${t.id}`}
                style={{
                  border: "1px solid var(--pg-border)", borderRadius: 12, overflow: "hidden",
                  cursor: creating ? "wait" : "pointer", background: "var(--pg-surface)",
                  padding: 0, textAlign: "left",
                  transition: "transform 140ms, box-shadow 140ms",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ height: 110, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={30} strokeWidth={1.6} style={{ color: "#1e3a8a" }} />
                </div>
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pg-text)" }}>{t.label}</div>
                  <div className="mono text-[11px]" style={{ color: "var(--pg-text-mute)", marginTop: 2 }}>{t.hint}</div>
                </div>
              </button>
            );
          })}
          {templates.slice(0, 3).map((t, i) => (
            <TemplateCard key={t.id} template={t} bg={TEMPLATE_GRADIENTS[i % TEMPLATE_GRADIENTS.length]}
                          onUse={() => create(t.id)} disabled={creating} />
          ))}
        </div>
      </div>

      {/* All rooms header */}
      <div className="px-8 pb-8">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>All rooms</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: 10, color: "var(--pg-text-mute)" }} />
              <input
                className="pg-input"
                style={{ paddingLeft: 30, width: 240 }}
                placeholder="Search room…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="rooms-search"
              />
            </div>
            <PragmaSelect
              value={status}
              onChange={setStatus}
              options={STATUS_FILTER}
              size="sm"
              testId="rooms-status-filter"
            />
            <button
              className="pg-btn pg-btn-primary"
              onClick={() => create("blank")}
              disabled={creating}
              data-testid="rooms-create-new"
            >
              <Plus size={14} /> Create new
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--pg-text-mute)" }} className="mono text-xs">loading…</div>
        ) : rows.length === 0 ? (
          <div className="pg-card" style={{ padding: "40px 20px", textAlign: "center" }} data-testid="rooms-empty">
            <DoorOpen size={30} style={{ margin: "0 auto 12px", color: "var(--pg-text-mute)" }} />
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
              {search ? "No rooms match your search" : "No rooms yet"}
            </div>
            <div className="text-sm" style={{ color: "var(--pg-text-mute)", maxWidth: 320, margin: "0 auto" }}>
              Rooms are branded microsites for each buyer — proposal, case studies, next steps, all in one link.
            </div>
          </div>
        ) : (
          <div className="pg-card" style={{ overflowX: "auto" }}>
            <table className="pg-table" data-testid="rooms-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Room name</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Last seen</th>
                  <th>Updated</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((room) => (
                  <tr
                    key={room.id}
                    onClick={() => navigate(`/rooms/${room.id}`)}
                    style={{ cursor: "pointer" }}
                    data-testid={`room-row-${room.id}`}
                  >
                    <td onClick={(e) => e.stopPropagation()} style={{ paddingLeft: 12 }}>
                      <button
                        type="button"
                        onClick={() => toggleStar(room)}
                        title={room.starred ? "Unstar" : "Star"}
                        data-testid={`room-star-${room.id}`}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          padding: 4, color: room.starred ? "#f59e0b" : "var(--pg-text-mute)",
                        }}
                      >
                        <Star size={16} fill={room.starred ? "#f59e0b" : "none"} />
                      </button>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{room.name}</div>
                    </td>
                    <td>
                      <StatusPill status={room.status} />
                    </td>
                    <td className="mono text-xs text-dim">{room.view_count || 0}</td>
                    <td className="text-dim text-sm">{fmtDate(room.last_seen_at)}</td>
                    <td className="text-dim text-sm">{fmtDate(room.updated_at)}</td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right", paddingRight: 12 }}>
                      {room.status !== "archived" && (
                        <button
                          type="button"
                          className="pg-btn pg-btn-ghost"
                          onClick={() => archiveRoom(room)}
                          title="Archive"
                          data-testid={`room-archive-${room.id}`}
                          style={{ padding: 6 }}
                        >
                          <Archive size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showTemplates && (
        <TemplatesGalleryModal
          templates={templates}
          canDelete={canManageTemplates}
          onClose={() => setShowTemplates(false)}
          onUse={(id) => { setShowTemplates(false); create(id); }}
          onDeleted={load}
          confirm={confirm}
        />
      )}
    </div>
  );
}

function TemplateCard({ template, bg, onUse, disabled }) {
  const initial = (template.name || "T").slice(0, 2).toUpperCase();
  return (
    <button
      type="button"
      onClick={onUse}
      disabled={disabled}
      data-testid={`template-card-${template.id}`}
      style={{
        border: "1px solid var(--pg-border)", borderRadius: 12, overflow: "hidden",
        cursor: disabled ? "wait" : "pointer", background: "var(--pg-surface)",
        padding: 0, textAlign: "left",
        transition: "transform 140ms, box-shadow 140ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{
        height: 110, background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{ fontSize: 30, fontWeight: 700, color: "#1e3a8a", letterSpacing: -1 }}>{initial}</span>
      </div>
      <div style={{ padding: "12px 14px" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pg-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {template.name}
        </div>
        <div className="mono text-[11px]" style={{ color: "var(--pg-text-mute)", marginTop: 2 }}>
          {template.section_count || 0} sections · Team template
        </div>
      </div>
    </button>
  );
}

function TemplatesGalleryModal({ templates, canDelete, onClose, onUse, onDeleted, confirm }) {
  const remove = async (t) => {
    const ok = await confirm({
      title: "Delete this template?",
      message: `"${t.name}" will be removed. Rooms already created from it are unaffected.`,
      destructive: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/rooms/templates/${t.id}`);
      toast.success("Template deleted");
      onDeleted?.();
    } catch { toast.error("Failed"); }
  };
  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="templates-modal-backdrop" />
      <div
        className="pg-card"
        role="dialog" aria-modal="true"
        data-testid="templates-modal"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(900px, 94vw)", maxHeight: "84vh", overflowY: "auto",
          padding: 0, zIndex: 210, borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--pg-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>All templates</h3>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" style={{ padding: 6 }} data-testid="templates-modal-close">
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 22 }}>
          {templates.length === 0 ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--pg-text-mute)" }}>
              <Sparkles size={26} style={{ margin: "0 auto 12px" }} /><br />
              No templates saved yet. Open any room and choose "Save as template" to build your library.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
              {templates.map((t, i) => (
                <div key={t.id} style={{ position: "relative" }}>
                  <TemplateCard template={t} bg={TEMPLATE_GRADIENTS[i % TEMPLATE_GRADIENTS.length]}
                                onUse={() => onUse(t.id)} disabled={false} />
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(t); }}
                      data-testid={`template-delete-${t.id}`}
                      title="Delete template"
                      style={{
                        position: "absolute", top: 8, right: 8,
                        background: "rgba(220,38,38,0.85)", color: "#fff",
                        border: "none", borderRadius: 6, padding: 5, cursor: "pointer",
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatusPill({ status }) {
  const meta = {
    draft:     { label: "Draft",     fg: "#64748b", bg: "rgba(100,116,139,0.14)", Icon: Circle },
    published: { label: "Active",    fg: "#059669", bg: "rgba(5,150,105,0.14)",   Icon: CheckCircle2 },
    archived:  { label: "Archived",  fg: "#94a3b8", bg: "rgba(148,163,184,0.14)", Icon: Archive },
  }[status] || { label: status, fg: "#64748b", bg: "rgba(100,116,139,0.14)", Icon: Circle };
  const { Icon } = meta;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999, background: meta.bg, color: meta.fg,
      fontSize: 12, fontWeight: 500,
    }}>
      <Icon size={12} /> {meta.label}
    </span>
  );
}
